import { useCallback, useEffect, useRef, useState } from 'react';
import { AppHeader } from '../components/AppHeader';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Icon } from '../components/Icon';
import { LoadingView } from '../components/LoadingView';
import { Notice } from '../components/Notice';
import type { MovieFrame, Project, SavedMovie } from '../domain/model';
import { formatMovieDuration, formatMovieSize, safeMovieTitle } from '../domain/model';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { exportFramesZip } from '../services/exportFramesZip';
import {
  deleteFinishedMovie,
  listFinishedMovies,
  purgeFinishedMovies,
  savedMoviePlaybackUrl,
  shareSavedMovie,
  undoDeleteFinishedMovie,
} from '../services/savedMovies';
import { deleteProject, getFrame, listProjects } from '../storage/projectRepository';

export const UNDO_WINDOW_MS = 6_000;

interface MovieItemProps {
  project: Project;
  onOpen: () => void;
  onDelete: () => void;
  onSave: () => void;
  saving: boolean;
}

function MovieItem({ project, onOpen, onDelete, onSave, saving }: MovieItemProps) {
  const [frame, setFrame] = useState<MovieFrame>();
  const url = useObjectUrl(frame?.blob);
  useEffect(() => {
    let alive = true;
    const lastId = project.frameIds.at(-1);
    if (lastId) void getFrame(lastId).then((value) => { if (alive) setFrame(value); });
    return () => { alive = false; };
  }, [project.frameIds]);
  return (
    <article className="movie-card">
      <button className="movie-open" type="button" onClick={onOpen} aria-label={`Open ${safeMovieTitle(project.title, project.createdAt)}`}>
        <div className="movie-thumb">{url ? <img src={url} alt="" /> : <Icon name="movies" />}</div>
        <div>
          <h2>{safeMovieTitle(project.title, project.createdAt)}</h2>
          <p>{project.frameIds.length} {project.frameIds.length === 1 ? 'picture' : 'pictures'}</p>
          <p>Changed {new Date(project.updatedAt).toLocaleDateString()}</p>
        </div>
      </button>
      {project.frameIds.length > 0 && (
        <button
          className="icon-button"
          type="button"
          onClick={onSave}
          disabled={saving}
          aria-label={`Save pictures from ${safeMovieTitle(project.title, project.createdAt)} to this device`}
        >
          <Icon name="download" />
        </button>
      )}
      <button className="icon-button danger-quiet" type="button" onClick={onDelete} aria-label={`Delete ${safeMovieTitle(project.title, project.createdAt)}`}><Icon name="trash" /></button>
    </article>
  );
}

interface FinishedItemProps {
  movie: SavedMovie;
  onPlay: () => void;
  onShare: () => void;
  onDelete: () => void;
  sharing: boolean;
}

function FinishedItem({ movie, onPlay, onShare, onDelete, sharing }: FinishedItemProps) {
  const posterUrl = useObjectUrl(movie.posterBlob);
  return (
    <article className="movie-card finished-card">
      <button className="movie-open" type="button" onClick={onPlay} aria-label={`Play ${movie.title}`}>
        <div className="movie-thumb finished-thumb">
          {posterUrl ? <img src={posterUrl} alt="" /> : <Icon name="film" />}
          <span className="thumb-play" aria-hidden="true"><Icon name="play" /></span>
        </div>
        <div>
          <h2>{movie.title}</h2>
          <p>{formatMovieDuration(movie.durationMs)} · {formatMovieSize(movie.sizeBytes)}</p>
          <p>Made {new Date(movie.createdAt).toLocaleDateString()}</p>
        </div>
      </button>
      <button className="icon-button" type="button" onClick={onShare} disabled={sharing} aria-label={`Share or download ${movie.title}`}><Icon name="share" /></button>
      <button className="icon-button danger-quiet" type="button" onClick={onDelete} aria-label={`Delete ${movie.title}`}><Icon name="trash" /></button>
    </article>
  );
}

function MoviePlayer({ movie, onClose }: { movie: SavedMovie; onClose: () => void }) {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    const playback = savedMoviePlaybackUrl(movie);
    setUrl(playback?.url);
    return () => { if (playback?.revoke) URL.revokeObjectURL(playback.url); };
  }, [movie]);
  return (
    <div className="dialog-shade player-shade" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <div className="dialog player-dialog" role="dialog" aria-modal="true" aria-label={`Playing ${movie.title}`}>
        <h2>{movie.title}</h2>
        {url
          ? <video className="saved-player" src={url} controls autoPlay playsInline aria-label={movie.title} />
          : <Notice tone="error" role="alert">This movie file is missing from this device.</Notice>}
        <div className="dialog-actions">
          <button className="button primary" type="button" onClick={onClose}>Done watching</button>
        </div>
      </div>
    </div>
  );
}

interface MoviesScreenProps {
  onBack: () => void;
  onOpen: (projectId: string) => void;
  onNew: () => void;
}

export function MoviesScreen({ onBack, onOpen, onNew }: MoviesScreenProps) {
  const [tab, setTab] = useState<'making' | 'finished'>('making');
  const [projects, setProjects] = useState<Project[]>();
  const [finished, setFinished] = useState<SavedMovie[]>();
  const [deleteTarget, setDeleteTarget] = useState<Project>();
  const [deleteMovieTarget, setDeleteMovieTarget] = useState<SavedMovie>();
  const [playing, setPlaying] = useState<SavedMovie>();
  const [undoMovie, setUndoMovie] = useState<SavedMovie>();
  const [savingId, setSavingId] = useState<string>();
  const [sharingId, setSharingId] = useState<string>();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const undoTimer = useRef<number | undefined>(undefined);

  const saveFrames = useCallback(async (project: Project) => {
    setError('');
    setNotice('');
    setSavingId(project.id);
    try {
      const outcome = await exportFramesZip(project);
      setNotice(outcome === 'downloaded'
        ? 'Your pictures were saved to this device as a zip file.'
        : 'Your pictures are ready to save or share.');
    } catch {
      setError('The pictures could not be saved. Please try again.');
    } finally {
      setSavingId(undefined);
    }
  }, []);

  const refresh = useCallback(async () => {
    const [projectList, finishedList] = await Promise.all([listProjects(), listFinishedMovies()]);
    setProjects(projectList);
    setFinished(finishedList);
  }, []);

  useEffect(() => {
    // Grace window: never hard-delete a tombstone the user could still undo.
    void purgeFinishedMovies(UNDO_WINDOW_MS)
      .catch(() => undefined)
      .then(refresh)
      .catch(() => setError('Saved movies could not be opened on this device.'));
    return () => window.clearTimeout(undoTimer.current);
  }, [refresh]);

  async function shareMovie(movie: SavedMovie) {
    setError('');
    setNotice('');
    setSharingId(movie.id);
    try {
      const outcome = await shareSavedMovie(movie);
      setNotice(outcome === 'downloaded' ? 'Your movie was downloaded to this device.' : 'Your movie is ready to save or share.');
    } catch {
      setError('The movie could not be shared. Please try again.');
    } finally {
      setSharingId(undefined);
    }
  }

  async function removeMovie(movie: SavedMovie) {
    setDeleteMovieTarget(undefined);
    setError('');
    try {
      await deleteFinishedMovie(movie.id);
      setUndoMovie(movie);
      setNotice('Movie deleted.');
      window.clearTimeout(undoTimer.current);
      undoTimer.current = window.setTimeout(() => setUndoMovie(undefined), UNDO_WINDOW_MS);
      await refresh();
    } catch {
      setError('The movie could not be deleted. Please try again.');
    }
  }

  async function undoRemoveMovie() {
    if (!undoMovie) return;
    window.clearTimeout(undoTimer.current);
    try {
      await undoDeleteFinishedMovie(undoMovie.id);
      setUndoMovie(undefined);
      setNotice('Movie put back!');
      await refresh();
    } catch {
      setError('The movie could not be put back.');
    }
  }

  const totalBytes = finished?.reduce((sum, movie) => sum + movie.sizeBytes, 0) ?? 0;

  if (!projects && !finished && !error) return <LoadingView label="Finding your movies…" />;
  return (
    <div className="screen-shell">
      <AppHeader title="My Movies" onBack={onBack} backLabel="Back to home" />
      <main className="movies-screen">
        <div className="tab-row" role="tablist" aria-label="Movie lists">
          <button className={`tab ${tab === 'making' ? 'selected' : ''}`} type="button" role="tab" aria-selected={tab === 'making'} onClick={() => setTab('making')}>
            <Icon name="camera" /> Still Making {projects?.length ? `(${projects.length})` : ''}
          </button>
          <button className={`tab ${tab === 'finished' ? 'selected' : ''}`} type="button" role="tab" aria-selected={tab === 'finished'} onClick={() => setTab('finished')}>
            <Icon name="film" /> Finished Movies {finished?.length ? `(${finished.length})` : ''}
          </button>
        </div>
        {error && <Notice tone="error" role="alert">{error}</Notice>}
        {notice && (
          <Notice tone="success" role="status">
            {notice}
            {undoMovie && <button className="notice-action" type="button" onClick={() => { void undoRemoveMovie(); }}><Icon name="undo" /> Undo</button>}
          </Notice>
        )}
        {tab === 'making' && (projects?.length ? (
          <div className="movie-list">
            {projects.map((project) => (
              <MovieItem
                key={project.id}
                project={project}
                onOpen={() => onOpen(project.id)}
                onDelete={() => setDeleteTarget(project)}
                onSave={() => { void saveFrames(project); }}
                saving={savingId === project.id}
              />
            ))}
          </div>
        ) : (
          <section className="empty-state">
            <div className="big-friendly-icon"><Icon name="movies" /></div>
            <h2>No movies yet</h2>
            <p>Your movies in progress will appear here.</p>
          </section>
        ))}
        {tab === 'finished' && (finished?.length ? (
          <>
            <p className="library-size">Your finished movies use {formatMovieSize(totalBytes)} on this device.</p>
            <div className="movie-list">
              {finished.map((movie) => (
                <FinishedItem
                  key={movie.id}
                  movie={movie}
                  onPlay={() => setPlaying(movie)}
                  onShare={() => { void shareMovie(movie); }}
                  onDelete={() => setDeleteMovieTarget(movie)}
                  sharing={sharingId === movie.id}
                />
              ))}
            </div>
          </>
        ) : (
          <section className="empty-state">
            <div className="big-friendly-icon"><Icon name="film" /></div>
            <h2>No finished movies yet</h2>
            <p>When you finish a movie, choose “Keep in My Movies” and it will appear here to watch any time.</p>
          </section>
        ))}
        <button className="button primary big-button" type="button" onClick={onNew}><Icon name="camera" /> Make a New Movie</button>
      </main>
      {playing && <MoviePlayer movie={playing} onClose={() => setPlaying(undefined)} />}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this whole movie?"
        message="All of its pictures will be removed from this device. Finished movies you kept will stay. This cannot be undone."
        confirmLabel="Delete movie"
        dangerous
        onCancel={() => setDeleteTarget(undefined)}
        onConfirm={() => {
          if (!deleteTarget) return;
          void deleteProject(deleteTarget.id)
            .then(refresh)
            .catch(() => setError('The movie could not be deleted. Please try again.'))
            .finally(() => setDeleteTarget(undefined));
        }}
      />
      <ConfirmDialog
        open={Boolean(deleteMovieTarget)}
        title="Delete this finished movie?"
        message="It will be removed from this device. You can undo this right after."
        confirmLabel="Delete movie"
        dangerous
        onCancel={() => setDeleteMovieTarget(undefined)}
        onConfirm={() => { if (deleteMovieTarget) void removeMovie(deleteMovieTarget); }}
      />
    </div>
  );
}
